import s from './OwnerSubscriptionPage.module.sass'

import React, { useState } from 'react'

import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { Checkbox } from '@shared/ui/Form/Single/Checkbox'
import { Title } from '@shared/ui/Typography/Title'
import { Text } from '@shared/ui/Typography/Text'
import { Button } from '@shared/ui/Actions/Button'
import IconCheckmark from '@icons/icon-checkbox-checkmark.svg'
import IconPlus from '@icons/icon-counter-plus.svg'

import { LK_OPTIONS, LK_PRO_OPTIONS, type PromotionState, calculateDiscountedPrice } from '@shared/types/promotion'
import { FloatingSummary } from '@shared/ui/Overlays/FloatingSummary'
import FAQ from './FAQ'
import { SuccessfulRegistrationModal } from '@pages/OwnerLanding/RegistrationForm/SuccessfulRegistrationModal/SuccessfulRegistrationModal'
import { useAppSelector } from '@app/model/store'

interface OwnerSubscriptionPageProps {
  slug: string
  restId: string
  currentPage: string
  data: any
  global_data: any
}

/**
 * @function OwnerSubscriptionPage - Подписка
 */
const OwnerSubscriptionPage = (props: OwnerSubscriptionPageProps) => {
  const stateUser = useAppSelector((state) => state.global.user.data)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [subscriptionState, setSubscriptionState] = useState<PromotionState>({})

  //тут инфа с бека
  const basicTariff = true
  const contract = 'Договор АФИША-Д-KASS-2025-4135 от 27.06.2025'
  const expireDateString = '25.09.2025'

  const handleCheckboxChange = (option: NonNullable<PromotionState['lkOptions']>[number], multiselect = false) => {
    const currentSection = (subscriptionState['lkOptions'] as (typeof option)[] | undefined) || []
    const newOptions = multiselect
      ? currentSection.includes(option)
        ? currentSection.filter((item) => item.id !== option.id)
        : [...currentSection, option]
      : currentSection.includes(option)
      ? []
      : [option]

    setSubscriptionState((prev) => ({
      ...prev,
      ['lkOptions']: newOptions,
    }))
  }

  return (
    <ErrorBoundary>
      {isSuccessModalOpen && (
        <SuccessfulRegistrationModal email={stateUser?.email || ''} onClose={() => setIsSuccessModalOpen(false)} />
      )}

      <div className={s['page-content']}>
        <div className={s['subscription-container']}>
          <div className={s['active-subscription']}>
            <Title tag='h3' sizes='h5'>
              Активная подписка
            </Title>

            <div className={s['subscription-card']}>
              <div className={s['subscription-info']}>
                <div className={s['tariff-info']}>
                  <Text sizes='ML'>{basicTariff ? 'Тариф «Базовый»' : 'Тариф «ПРО»'}</Text>

                  <Text sizes='M' className={s['contract-info']}>
                    {contract}
                  </Text>
                </div>

                <div className={s['access-badge']}>
                  <Text sizes='M'>Доступ открыт до {expireDateString}</Text>
                </div>
              </div>
            </div>
          </div>

          {basicTariff && (
            <div className={s['info-notice']}>
              <div className={s['notice-icon']}>!</div>

              <Text sizes='S' className={s['notice-content']}>
                Мы обновили систему — теперь вы сами выбираете подходящий тариф для управления и продвижения вашего
                ресторана. Все, кто ранее оплачивал доступ, автоматически перешли на тариф «Базовый» — он остается
                активным. Хотите привлечь больше гостей и сделать ваш ресторан заметнее? Переходите на тариф «ПРО» — он
                откроет больше возможностей для продвижения. Ознакомиться с другими инструментами теперь можно прямо в
                личном кабинете, в разделе «Продвижение».
              </Text>
            </div>
          )}
        </div>

        <div className={s['subscription-options']}>
          <div className={s['block']}>
            <Title tag='h3' sizes='h5'>
              {basicTariff ? 'Перейти на тариф «ПРО»' : 'Продлить тариф «ПРО»'}
            </Title>

            <div className={s['subscription-content']}>
              <div className={s['description-card']}>
                <Text sizes='M'>
                  Дополнительные источники трафика и привлечения потенциальных гостей помимо базового функционала
                </Text>
              </div>

              <div className={s['features-list']}>
                <div className={s['feature-item']}>
                  <IconCheckmark className={s['checkmark-icon']} />
                  <Text sizes='M'>Публикация новостей и обновлений</Text>
                </div>

                <div className={s['feature-item']}>
                  <IconCheckmark className={s['checkmark-icon']} />
                  <Text sizes='M'>Работа с отзывами</Text>
                </div>

                <div className={s['feature-item']}>
                  <IconCheckmark className={s['checkmark-icon']} />
                  <Text sizes='M'>Статистика по просмотрам</Text>
                </div>

                <div className={s['feature-item']}>
                  <IconPlus className={s['plus-icon']} />
                  <Text sizes='M'>Сервис бронирования столов</Text>
                </div>

                <div className={s['feature-item']}>
                  <IconPlus className={s['plus-icon']} />
                  <Text sizes='M'>Маркетинговая поддержка</Text>
                </div>

                <div className={s['feature-item']}>
                  <IconPlus className={s['plus-icon']} />
                  <Text sizes='M'>Размещение на платформах партнеров</Text>
                </div>

                <Button.Link
                  target='_blank'
                  href='https://www.afisha.ru/rests/documents/restorator/2025_05_27/more_pro.pdf'
                  sizes='XL'
                  mode='white-border'
                  className={s['more-button']}>
                  Подробнее
                </Button.Link>
              </div>

              <div className={s['options-list']}>
                {LK_PRO_OPTIONS.map((option) => (
                  <div key={option.id} className={s['option-card']}>
                    <div className={s['checkbox-group']}>
                      <Checkbox
                        mode='checkbox'
                        option={{
                          id: option.id,
                          name: option.id,
                        }}
                        checked={subscriptionState.lkOptions?.includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                      />
                      <Text sizes='M' className={s['option-label']} onClick={() => handleCheckboxChange(option)}>
                        {option.label}
                      </Text>
                    </div>

                    <div className={s['price-section']}>
                      {'discount' in option && option.discount && (
                        <div className={s['discount-badge']}>
                          <Text sizes='S' className={s['discount-text']}>
                            –{option.discount}%
                          </Text>
                        </div>
                      )}

                      <Text sizes='M' className={s['price-text']}>
                        {calculateDiscountedPrice(option.price, option.discount).toLocaleString('ru-RU')} ₽
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={s['block']}>
            <Title tag='h3' sizes='h5'>
              {basicTariff ? 'Продлить тариф «Базовый»' : 'Тариф «Базовый»'}
            </Title>

            <div className={s['subscription-content']}>
              <div className={s['subscription-column']}>
                <div className={s['description-card']}>
                  <Text sizes='M'>Базовый набор функций для презентации ресторана на сайте «Афиши Рестораны»</Text>
                </div>

                <div className={s['features-list']}>
                  <div className={s['feature-item']}>
                    <IconCheckmark className={s['checkmark-icon']} />
                    <Text sizes='M'>Публикация новостей и обновлений</Text>
                  </div>
                  <div className={s['feature-item']}>
                    <IconCheckmark className={s['checkmark-icon']} />
                    <Text sizes='M'>Работа с отзывами</Text>
                  </div>
                  <div className={s['feature-item']}>
                    <IconCheckmark className={s['checkmark-icon']} />
                    <Text sizes='M'>Статистика по просмотрам</Text>
                  </div>
                  <Button.Link
                    target='_blank'
                    href='https://www.afisha.ru/rests/documents/restorator/2025_05_27/more_basic.pdf'
                    sizes='XL'
                    mode='white-border'
                    className={s['more-button']}>
                    Подробнее
                  </Button.Link>
                </div>

                <div className={s['options-list']}>
                  {LK_OPTIONS.map((option) => (
                    <div key={option.id} className={s['option-card']}>
                      <div className={s['checkbox-group']}>
                        <Checkbox
                          mode='checkbox'
                          option={{
                            id: option.id,
                            name: option.id,
                          }}
                          checked={subscriptionState.lkOptions?.includes(option)}
                          onChange={() => handleCheckboxChange(option)}
                        />

                        <Text className={s['option-label']} onClick={() => handleCheckboxChange(option)} sizes='M'>
                          {option.label}
                        </Text>
                      </div>

                      <div className={s['price-section']}>
                        <Text sizes='M' className={s['price-text']}>
                          {option.price.toLocaleString('ru-RU')} ₽
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Вопросы и ответы здесь */}
        <FAQ />
      </div>

      <FloatingSummary
        title='Доступ к личному кабинету'
        selectionState={subscriptionState}
        setSelectionState={setSubscriptionState}
        onSubmit={() => {
          setIsSending(true)

          setTimeout(() => {
            setIsSuccessModalOpen(true)
            setIsSending(false)
            setSubscriptionState({})
          }, 1000)
        }}
        showCheckboxes={false}
        isLoading={isSending}
      />
    </ErrorBoundary>
  )
}

export default OwnerSubscriptionPage
